import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { AtlasAIChat, useAtlasAIChat } from '@/components/AtlasAIChat';
import EnvBanner from '@/components/EnvBanner';
import { getFAQs } from '@/api/portal';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import { getNavigationForRoles, NavSection } from '@/config/navigation';
import { useIntakeAccess } from '@/hooks/useIntake';

// ── ProfileAvatar ────────────────────────────────────────────────────────────
function ProfileAvatar({ name, photoUrl, size = 'md' }: { name: string; photoUrl?: string | null; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  const sizeClasses = {
    sm: 'w-7 h-7 text-xs',
    md: 'w-9 h-9 text-sm',
    lg: 'w-12 h-12 text-base',
  };

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className={`${sizeClasses[size]} rounded-full object-cover flex-shrink-0`}
      />
    );
  }

  return (
    <span
      className={`${sizeClasses[size]} rounded-full bg-jm-yellow text-jm-charcoal font-montserrat font-bold flex items-center justify-center select-none flex-shrink-0`}
    >
      {initials}
    </span>
  );
}

// ── UserDropdown ──────────────────────────────────────────────────────────────
function UserDropdown({ onLogout }: { onLogout: () => void }) {
  const { user } = useAuth();
  const { data: photoUrl } = useProfilePhoto();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2.5 px-2 py-1.5 rounded-full hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jm-yellow"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <ProfileAvatar name={user.name} photoUrl={photoUrl} />
        <span className="hidden md:block text-white/90 font-work-sans text-sm font-medium max-w-[140px] truncate">
          {user.firstName}
        </span>
        <svg
          className={`w-3.5 h-3.5 text-white/50 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-jm-border-light overflow-hidden z-50 animate-slide-down">
          <div className="px-5 py-4 bg-jm-surface border-b border-jm-border-light">
            <div className="flex items-center gap-3">
              <ProfileAvatar name={user.name} photoUrl={photoUrl} size="lg" />
              <div className="min-w-0">
                <p className="font-montserrat font-semibold text-jm-text text-sm truncate">{user.name}</p>
                <p className="text-xs text-jm-text-secondary truncate">{user.email}</p>
                {user.jobTitle && <p className="text-xs text-jm-text-tertiary truncate mt-0.5">{user.jobTitle}</p>}
              </div>
            </div>
          </div>

          {user.roles.length > 0 && (
            <div className="px-5 py-3 border-b border-jm-border-light">
              <div className="flex flex-wrap gap-1.5">
                {user.roles.map((role) => (
                  <span
                    key={role}
                    className="text-xs px-2.5 py-0.5 bg-jm-charcoal/5 text-jm-charcoal-light rounded-full font-work-sans"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="py-1.5">
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-jm-text-secondary hover:bg-jm-surface transition-colors font-work-sans"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  );
}

/**
 * TEME mascot icon (Arul 2026-05-11). Renders the real cocker-spaniel
 * illustration at `/teme-mascot.jpg`; falls back to an inline SVG if
 * the asset is missing.
 */
function TemeIcon({ className }: { className?: string }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (imgFailed) {
    return (
      <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="7" cy="11" rx="4" ry="6" fill="#b88456" />
        <ellipse cx="25" cy="11" rx="4" ry="6" fill="#b88456" />
        <circle cx="16" cy="16" r="9" fill="#d8a878" />
        <ellipse cx="16" cy="20" rx="4.5" ry="3.5" fill="#f0d4ad" />
        <ellipse cx="16" cy="18.5" rx="1.4" ry="1" fill="#2a2a2a" />
        <circle cx="12.5" cy="14.5" r="1.3" fill="#2a2a2a" />
        <circle cx="19.5" cy="14.5" r="1.3" fill="#2a2a2a" />
        <circle cx="12.9" cy="14.1" r="0.45" fill="#fff" />
        <circle cx="19.9" cy="14.1" r="0.45" fill="#fff" />
        <path d="M13.5 21.5 Q16 23.2 18.5 21.5" stroke="#2a2a2a" strokeWidth="1.2" strokeLinecap="round" fill="none" />
      </svg>
    );
  }
  return (
    <img
      src="/teme-mascot.jpg"
      alt="TEME"
      className={className}
      onError={() => setImgFailed(true)}
    />
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ── GlobalSearch ──────────────────────────────────────────────────────────────
interface FAQResult { _id: string; question: string; category: string; }

function GlobalSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value.trim()), 300);
    return () => clearTimeout(t);
  }, [value]);

  const { data } = useQuery({
    queryKey: ['global-search', debouncedValue],
    queryFn: () => getFAQs({ search: debouncedValue }),
    enabled: debouncedValue.length >= 2,
    staleTime: 30_000,
  });

  const rawResults = (data as { data?: FAQResult[] } | FAQResult[] | undefined);
  const results: FAQResult[] = (
    Array.isArray(rawResults) ? rawResults :
    Array.isArray((rawResults as { data?: FAQResult[] })?.data) ? (rawResults as { data: FAQResult[] }).data :
    []
  ).slice(0, 5);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function commit(q: string) {
    navigate(`/ask-andrew?q=${encodeURIComponent(q)}`);
    setValue('');
    setOpen(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (value.trim()) commit(value.trim());
  }

  const showDropdown = open && debouncedValue.length >= 2 && results.length > 0;

  return (
    <div ref={containerRef} className="hidden md:block relative w-full max-w-sm">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <input
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); setOpen(true); }}
            onFocus={() => { if (value.trim().length >= 2) setOpen(true); }}
            onKeyDown={(e) => { if (e.key === 'Escape') { setValue(''); setOpen(false); } }}
            placeholder="Search Atlas..."
            className="w-full bg-white/8 text-white placeholder-white/30 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm font-work-sans focus:outline-none focus:bg-white/12 focus:border-white/25 transition-all"
          />
        </div>
      </form>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-jm-border-light overflow-hidden z-50 animate-slide-down">
          {results.map((faq) => (
            <button
              key={faq._id}
              onClick={() => commit(faq.question)}
              className="w-full text-left px-4 py-3 hover:bg-jm-surface transition-colors flex items-start gap-3 border-b border-jm-border-light last:border-b-0"
            >
              <span className="mt-0.5 text-xs font-work-sans font-medium px-2 py-0.5 rounded-full bg-jm-charcoal/5 text-jm-charcoal-light flex-shrink-0">
                {faq.category}
              </span>
              <span className="text-sm font-work-sans text-jm-text leading-snug line-clamp-2">{faq.question}</span>
            </button>
          ))}
          <div className="px-4 py-2 bg-jm-surface text-xs font-work-sans text-jm-text-tertiary">
            Press <kbd className="px-1.5 py-0.5 bg-white rounded text-jm-text-secondary font-mono text-[10px] border border-jm-border-light">Enter</kbd> to ask TEME
          </div>
        </div>
      )}
    </div>
  );
}

// ── NavDropdown ──────────────────────────────────────────────────────────────
interface NavDropdownItem {
  to: string;
  label: string;
  description?: string;
}

interface NavDropdownProps {
  label: string;
  items: NavDropdownItem[];
}

function NavDropdown({ label, items }: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isActive = items.some((item) => location.pathname === item.to || location.pathname.startsWith(item.to + '/'));

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  function handleMouseEnter() {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  }

  function handleMouseLeave() {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  }

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={[
          'flex items-center gap-1 px-3 py-2 rounded-full text-sm font-work-sans font-medium transition-all duration-200',
          isActive
            ? 'text-white bg-white/15'
            : 'text-white/70 hover:text-white hover:bg-white/8',
        ].join(' ')}
      >
        <span>{label}</span>
        <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-jm-border-light overflow-hidden z-50 animate-slide-down">
          <div className="py-1.5">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive: active }: { isActive: boolean }) => [
                  'flex flex-col px-4 py-3 transition-colors',
                  active ? 'bg-jm-surface' : 'hover:bg-jm-surface/60',
                ].join(' ')}
              >
                <span className={`text-sm font-work-sans font-medium ${location.pathname === item.to ? 'text-jm-charcoal' : 'text-jm-text'}`}>
                  {item.label}
                </span>
                {item.description && (
                  <span className="text-xs text-jm-text-tertiary mt-0.5">{item.description}</span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── NavLink (single) ─────────────────────────────────────────────────────────
function TopNavLink({ to, label, end }: { to: string; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }: { isActive: boolean }) => [
        'px-3 py-2 rounded-full text-sm font-work-sans font-medium transition-all duration-200',
        isActive
          ? 'text-white bg-white/15'
          : 'text-white/70 hover:text-white hover:bg-white/8',
      ].join(' ')}
    >
      {label}
    </NavLink>
  );
}

// ── Mobile Menu ──────────────────────────────────────────────────────────────
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  sections: NavSection[];
}

function MobileMenu({ isOpen, onClose, sections }: MobileMenuProps) {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-x-0 top-[56px] bottom-0 bg-white z-50 overflow-y-auto animate-slide-down">
        <div className="px-6 py-4">
          <NavLink
            to="/"
            end
            onClick={onClose}
            className={({ isActive }: { isActive: boolean }) => [
              'block py-3 text-lg font-montserrat font-semibold transition-colors',
              isActive ? 'text-jm-charcoal' : 'text-jm-text-secondary',
            ].join(' ')}
          >
            Home
          </NavLink>

          {sections.map((section) => (
            <div key={section.title} className="mt-6">
              <p className="text-xs font-montserrat font-semibold uppercase tracking-widest text-jm-text-tertiary mb-2">
                {section.title}
              </p>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={({ isActive }: { isActive: boolean }) => [
                    'block py-3 text-base font-work-sans border-b border-jm-border-light transition-colors',
                    isActive ? 'text-jm-charcoal font-medium' : 'text-jm-text-secondary',
                  ].join(' ')}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────
export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const aiChat = useAtlasAIChat();
  const location = useLocation();

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Single source of truth: navigation config in @/config/navigation.ts.
  // To add a new module, edit that file — no Layout edits needed.
  // ATLAS-107a: Improve (Product Ideas) shows only when intake's /me gives a role. The answer is cached
  // for the session and shared with the intake pages, so this adds one /me call per visit, not per page.
  const intakeAccess = useIntakeAccess();
  const visibleSections = getNavigationForRoles(user?.roles ?? [], user?.email, {
    intake: intakeAccess.state === 'ready',
  });

  return (
    <div className="min-h-screen flex flex-col bg-jm-surface">
      {/* High-visibility test-environment indicator. Renders only when
          hostname is not the prod URL — invisible on atlas.jmtest.com. */}
      <EnvBanner />

      {/* ── Top Navigation ──────────────────────────────────────────────────── */}
      <header className="glass-nav sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto flex items-center h-14 px-4 lg:px-6 gap-4">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="md:hidden p-2 -ml-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle navigation"
          >
            {mobileMenuOpen ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            )}
          </button>

          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <img
              src="https://jmtest.com/wp-content/uploads/2017/04/JM-Test-Systems-logo.jpg"
              alt="JM Test Systems"
              className="h-7 w-auto rounded-md object-contain bg-white px-1"
              loading="eager"
            />
            <span className="font-montserrat font-bold text-lg text-white tracking-wide hidden sm:block">
              Atlas
            </span>
          </Link>

          {/* Desktop navigation — driven by visibleSections (config in @/config/navigation.ts) */}
          <nav className="hidden md:flex items-center gap-1 ml-6">
            <TopNavLink to="/" label="Home" end />
            {visibleSections.map((section) =>
              section.items.length === 1 ? (
                // Single-item sections render as a direct link (e.g. Admin → /admin).
                <TopNavLink
                  key={section.id}
                  to={section.items[0]!.to}
                  label={section.title}
                />
              ) : (
                <NavDropdown
                  key={section.id}
                  label={section.title}
                  items={section.items.map((i) => ({
                    to: i.to,
                    label: i.label,
                    description: i.description,
                  }))}
                />
              ),
            )}
          </nav>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Search */}
          <GlobalSearch />

          {/* Right actions */}
          <div className="flex items-center gap-1">
            <button
              className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/8 transition-colors"
              aria-label="Notifications"
            >
              <BellIcon className="w-5 h-5" />
            </button>
            <UserDropdown onLogout={() => { void logout(); }} />
          </div>
        </div>
      </header>

      {/* ── Mobile Menu ─────────────────────────────────────────────────────── */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sections={visibleSections}
      />

      {/* ── Main Content ────────────────────────────────────────────────────── */}
      <main className="flex-1">
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 py-6 md:py-10">
          {children}
        </div>
      </main>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="border-t border-jm-border-light bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img
              src="https://jmtest.com/wp-content/uploads/2017/04/JM-Test-Systems-logo.jpg"
              alt="JM Test Systems"
              className="h-5 w-auto rounded object-contain"
            />
            <span className="text-xs text-jm-text-tertiary font-work-sans">
              &copy; {new Date().getFullYear()} JM Test Systems. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-jm-text-tertiary font-work-sans">
            <span>Atlas Employee Portal</span>
            <span className="hidden sm:inline">&middot;</span>
            <span className="hidden sm:inline">Powered by JM Test Technology</span>
          </div>
        </div>
      </footer>

      {/* ── TEME Floating Action Button ─────────────────────────────────────── */}
      <button
        onClick={aiChat.toggle}
        className={[
          'fixed bottom-6 right-6 z-40',
          'flex items-center gap-2.5 pl-2 pr-5 py-2',
          'bg-jm-charcoal text-white rounded-full shadow-lg',
          'hover:bg-jm-charcoal-dark hover:shadow-xl',
          'transition-all duration-200 font-montserrat font-bold text-sm',
          aiChat.isOpen ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100',
        ].join(' ')}
        aria-label="Ask TEME"
        title="Ask TEME — Test Equipment Made Easy"
      >
        <div className="w-9 h-9 rounded-full bg-jm-yellow flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-jm-charcoal-dark">
          <TemeIcon className="w-7 h-7" />
        </div>
        <span>Ask TEME</span>
      </button>

      {/* ── TEME Chat Widget ──────────────────────────────────────────────── */}
      <AtlasAIChat isOpen={aiChat.isOpen} onClose={aiChat.close} />
    </div>
  );
}
